import React, { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../../states/containers/store'
import { ResponseMonthlyTeamReport, ResponseMonthlyMemberReport } from '../../../../../states/types/report'
import Loading from '../../../common/Loading'
import { secToHourMinute } from '../../../../../utils/date'
import Deviation from '../Deviation'
import DeviationHeader from '../DeviationHeader'
import MonthlyBarGraph from './MonthlyBarGraph'
import Tips from '../../../common/Tips'

interface Heatmap {
  type: string
  range: string
  target: string
  id: number
  title: string
  legend: string
  startColor: string
  endColor: string
  minimum: number
  displaySearchLink: boolean
  name: string
}

type ScoreColumnName = 'process_score' | 'resource_score' | 'improvement_score' | 'legacy_score' | 'stress_score'

interface Scores {
  label: ScoreColumnName
  current: number
  past: number | null
  company: number
  qasee?: number
  team?: number
  occupation?: number
  distributions: {
    from: number
    to: number
    percent: number
  }[]
}

interface Props {
  target: string
  heatmapModalOpenCallback: (heatmap: Heatmap) => void
}

const MonthlyStress: FC<Props> = (props) => {
  const {
    target,
    heatmapModalOpenCallback,
  } = props

  const {
    report,
  } = useSelector((state: RootState) => (state))

  const { t } = useTranslation()

  const subContentsRef = useRef<HTMLDivElement>(null)
  const subContentsLastRef = useRef<HTMLDivElement>(null)

  const [ruledLineHeight, setRuledLineHeight] = useState<number>(0)

  const [data, setData] = useState<ResponseMonthlyTeamReport | ResponseMonthlyMemberReport>()
  const [scores, setScores] = useState<Scores>()
  const [maxVerticalLabelWorkTime, setMaxVerticalLabelWorkTime] = useState<number>(0)
  const [maxVerticalLabelBizPoint, setMaxVerticalLabelBizPoint] = useState<number>(0)
  const [color, setColor] = useState<string>()
  const [workStyleId, setWorkStyleId] = useState<number>(0)
  const [occupationId, setOccupationId] = useState<number>(0)

  const targetIndicatorValues = (indicatorName: string) => ({
    current: data?.indicators?.find((value) => value.label === indicatorName)?.current ?? null,
    prev: data?.indicators?.find((value) => value.label === indicatorName)?.past ?? null,
    team: data?.indicators?.find((value) => value.label === indicatorName)?.team ?? null,
    company: data?.indicators?.find((value) => value.label === indicatorName)?.company ?? null,
    occupation: data?.indicators?.find((value) => value.label === indicatorName)?.occupation ?? null,
    qasee: data?.indicators?.find((value) => value.label === indicatorName)?.qasee ?? null,
    checked: data?.indicators?.find((value) => value.label === indicatorName)?.checked || false,
    appropriate_from: data?.indicators?.find((value) => value.label === indicatorName)?.appropriate_from ?? null,
    appropriate_to: data?.indicators?.find((value) => value.label === indicatorName)?.appropriate_to ?? null,
  })

  const canvasBizPointRef = useRef<HTMLCanvasElement>(null)
  const canvasWorkTimeRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (!subContentsRef.current?.offsetHeight || !subContentsLastRef.current?.offsetHeight) return
    setRuledLineHeight(subContentsRef.current?.offsetHeight - (subContentsLastRef.current?.offsetHeight / 2) + 40)
  }, [subContentsRef.current?.offsetHeight, subContentsLastRef.current?.offsetHeight])

  useEffect(() => {
    if (!target) return
    if (report.isLoading) return
    if (target === "team") {
      if (!report.monthlyTeamReport) return
      setData(report.monthlyTeamReport)
      setScores(report.monthlyTeamReport.scores?.find((score) => score.label === "stress_score"))
      setColor("")
      setWorkStyleId(0)
      setOccupationId(0)
    } else {
      if (!report.monthlyMemberReport) return
      setData(report.monthlyMemberReport)
      setScores(report.monthlyMemberReport.scores?.find((score) => score.label === "stress_score"))
      setColor(report.monthlyMemberReport.member?.applied_occupation?.icon_color)
      setWorkStyleId(0)
      setOccupationId(0)
    }
  }, [target, report])

  useEffect(() => {
    if (!data?.work_volume_variation) return
    const maxWorkTimeValue = Math.max(...data.work_volume_variation.map((value) => (
      value.one_day_work_time
    )))
    const maxBizPointValue = Math.max(...data.work_volume_variation.map((value) => (
      value.one_day_biz_point
    )))
    setMaxVerticalLabelWorkTime(Math.floor(maxWorkTimeValue / 3600 / 5) + 1)
    setMaxVerticalLabelBizPoint(Math.floor(maxBizPointValue / 10000) + 1)
  }, [data])

  useEffect(() => {
    if (!data?.work_volume_variation || !maxVerticalLabelWorkTime) return
    const width = canvasWorkTimeRef.current?.clientWidth || 0
    const height = canvasWorkTimeRef.current?.clientHeight || 0
    const canvas = canvasWorkTimeRef.current
    if (!canvas || !canvas.getContext) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    canvas.width = width
    canvas.height = height
    ctx.beginPath()
    ctx.lineWidth = 1
    ctx.strokeStyle = "#008ffb"
    data.work_volume_variation.forEach((value, index) => {
      ctx.lineTo(
        Math.floor(width * (1 - (0.01 * (index * 18 + 5)))),
        height - Math.floor(height * (value.one_day_work_time / 3600 / (maxVerticalLabelWorkTime * 5))),
      )
    })
    ctx.stroke()
  }, [data, maxVerticalLabelWorkTime])

  useEffect(() => {
    if (!data?.work_volume_variation || !maxVerticalLabelBizPoint) return
    const width = canvasBizPointRef.current?.clientWidth || 0
    const height = canvasBizPointRef.current?.clientHeight || 0
    const canvas = canvasBizPointRef.current
    if (!canvas || !canvas.getContext) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    canvas.width = width
    canvas.height = height
    ctx.beginPath()
    ctx.lineWidth = 1
    ctx.strokeStyle = "#008ffb"
    data.work_volume_variation.forEach((value, index) => {
      ctx.lineTo(
        Math.floor(width * (1 - (0.01 * (index * 18 + 5)))),
        height - Math.floor(height * (value.one_day_biz_point / (maxVerticalLabelBizPoint * 10000))),
      )
    })
    ctx.stroke()
  }, [data, maxVerticalLabelBizPoint])

  return (
    <>
      {
        report.isLoading ? (
          <>
            <Loading />
          </>
        ) : (
          <>
            <section id="monthly_stress" className="page_contents">
              <div
                className="vertical_ruler"
                style={{
                  height: `${ruledLineHeight}px`,
                }}
              />
              <div id="stress" className="content-block monthly">
                <div className="block-detail score">
                  <div className="header_area">
                    <div className="prefix">
                      エンゲージメント
                      <br />
                      【活動度】
                    </div>
                    <div className="title">
                      コンディション指数
                    </div>
                    <div className="point">
                      {scores?.current || 0}
                    </div>
                    <div className="description">
                      業務の負荷や働き方の変化、コミュニケーション割合からコンディションに対する評価を行なっています。
                    </div>
                  </div>
                  <MonthlyBarGraph
                    target={target}
                    value={{
                      current: scores?.current || 0,
                      past: scores?.past || 0,
                      team: scores?.team,
                      occupation: scores?.occupation,
                      company: scores?.company || 0,
                      qasee: scores?.qasee,
                      distributions: scores?.distributions || [],
                    }}
                  />
                </div>
              </div>
            </section>
            <section ref={subContentsRef}>
              <section className="inner_contents">
                <div className="horizontal_ruler" />
                <section className="page_contents">
                  <div className="content-block">
                    <div className="block-header">
                      <div className="title">
                        オーバーワーク
                      </div>
                    </div>
                    <div className="block-detail">
                      <DeviationHeader
                        target={target}
                        range="monthly"
                        work_style_id={workStyleId}
                        occupation_id={occupationId}
                      />
                      <Deviation
                        target={target}
                        title="overtime_work_time"
                        display_title="残業時間"
                        type="hour"
                        values={targetIndicatorValues("overtime_work_time")}
                        labels={[0, 20, 40, 60, 80]}
                        half
                        upper_limit
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink
                        help_text={t(`report.indicator_help.monthly.${target}.stress.overtime_work_time`)}
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="midnight_work_time"
                        display_title="深夜残業時間"
                        type="hour"
                        values={targetIndicatorValues("midnight_work_time")}
                        labels={[0, 2, 4, 6, 8]}
                        half
                        upper_limit
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink
                        help_text={t(`report.indicator_help.monthly.${target}.stress.midnight_work_time`)}
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="work_days"
                        display_title="勤務日数"
                        type="day"
                        values={targetIndicatorValues("work_days")}
                        labels={[0, 5, 10, 15, 20, 25, 30]}
                        upper_limit
                        appropriate={83.333}
                        appropriate_position={-50}
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink
                        help_text={t(`report.indicator_help.monthly.${target}.stress.work_days`)}
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="overtime_work_days"
                        display_title="残業回数"
                        type="count"
                        values={targetIndicatorValues("overtime_work_days")}
                        labels={[0, 6, 12, 18]}
                        half
                        upper_limit
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink
                        help_text={t(`report.indicator_help.monthly.${target}.stress.overtime_work_days`)}
                        color={color}
                      />
                    </div>
                  </div>
                </section>
              </section>
              <section className="inner_contents">
                <div className="horizontal_ruler" />
                <section className="page_contents">
                  <div className="content-block work_volume_variation">
                    <div className="block-header">
                      <div className="title">
                        働き方変化
                      </div>
                    </div>
                    <div className="block-detail">
                      <div className="work_volume_variation_graph">
                        <div className="left">
                          <div className="graph_title">
                            <Tips
                              description="平均勤務時間の推移"
                              className="detail_title"
                            >
                              <>
                                勤務時間の変化
                              </>
                            </Tips>
                          </div>
                          <div className="graph_area">
                            <table>
                              <tbody>
                                {
                                  Array(maxVerticalLabelWorkTime).fill('').map((_value, index) => (
                                    <tr
                                      // eslint-disable-next-line react/no-array-index-key
                                      key={`work_volume_variation_${index}`}
                                      style={{
                                        height: `${100 / (maxVerticalLabelWorkTime + 1)}%`,
                                      }}
                                    >
                                      <td />
                                    </tr>
                                  ))
                                }
                              </tbody>
                            </table>
                            <canvas
                              className="canvas"
                              ref={canvasWorkTimeRef}
                            />
                            {
                              Array(maxVerticalLabelWorkTime + 1).fill('').map((_value, index) => (
                                <div
                                   // eslint-disable-next-line react/no-array-index-key
                                  key={`work_volume_variation_${index}`}
                                  className={`vertical_label time label_${index}`}
                                  style={{
                                    bottom: `${(100 / (maxVerticalLabelWorkTime)) * index}%`,
                                  }}
                                >
                                  <div className="vertical_label_value">
                                    { index * 5 }
                                  </div>
                                </div>
                              ))
                            }
                            <div className="vertical_label unit">
                              時間
                            </div>
                            {
                              data?.work_volume_variation?.map((value, index) => (
                                <React.Fragment key={`work_volume_variation_${value.month}`}>
                                  <div
                                    className={`horizontal_label ${index === 0 || index === 5 ? 'year' : ''} ${index === 0 ? 'current' : ''}`}
                                    style={{
                                      right: `${index * 18 + 5}%`,
                                    }}
                                  >
                                    {value.month}
                                    <br />
                                    <span className="year">
                                      {value.year}
                                    </span>
                                  </div>
                                  <div
                                    className="past_mark"
                                    style={{
                                      top: `${100 - (value.one_day_work_time / 3600 / (maxVerticalLabelWorkTime * 5)) * 100}%`,
                                      right: `${index * 18 + 5}%`,
                                    }}
                                  />
                                  {
                                    index === 0 && (
                                      <div
                                        className="past_mark_current"
                                        style={{
                                          top: `${100 - (value.one_day_work_time / 3600 / (maxVerticalLabelWorkTime * 5)) * 100}%`,
                                          right: `${index * 18 + 5}%`,
                                        }}
                                      />
                                    )
                                  }
                                  <div
                                    className={`past_point ${index === 0 ? 'current' : ''}`}
                                    style={{
                                      top: `${100 - (value.one_day_work_time / 3600 / (maxVerticalLabelWorkTime * 5)) * 100}%`,
                                      right: `${index * 18 + 5}%`,
                                    }}
                                  >
                                    {secToHourMinute(value.one_day_work_time)}
                                  </div>
                                </React.Fragment>
                              ))
                            }
                          </div>
                        </div>
                        <div className="right">
                          <div className="graph_title">
                            <Tips
                              description="平均操作量の推移"
                              className="detail_title"
                            >
                              <>
                                平均操作量
                              </>
                            </Tips>
                          </div>
                          <div className="graph_area">
                            <table>
                              <tbody>
                                {
                                  Array(maxVerticalLabelBizPoint * 2).fill('').map((_value, index) => (
                                    <tr
                                      // eslint-disable-next-line react/no-array-index-key
                                      key={`work_volume_variation_${index}`}
                                      style={{
                                        height: `${100 / ((maxVerticalLabelBizPoint + 1) * 2)}%`,
                                      }}
                                    >
                                      <td />
                                    </tr>
                                  ))
                                }
                              </tbody>
                            </table>
                            <canvas
                              className="canvas"
                              ref={canvasBizPointRef}
                            />
                            {
                              Array(maxVerticalLabelBizPoint + 1).fill('').map((_value, index) => (
                                <div
                                  // eslint-disable-next-line react/no-array-index-key
                                  key={`work_volume_variation_${index}`}
                                  className={`vertical_label biz label_${index}`}
                                  style={{
                                    bottom: `${(100 / (maxVerticalLabelBizPoint)) * index}%`,
                                  }}
                                >
                                  <div className="vertical_label_value">
                                    { (index * 10000).toLocaleString() }
                                  </div>
                                </div>
                              ))
                            }
                            <div className="vertical_label unit biz">
                              biz
                            </div>
                            {
                              data?.work_volume_variation?.map((value, index) => (
                                <React.Fragment key={`work_volume_variation_${value.month}`}>
                                  <div
                                    className={`horizontal_label ${index === 0 || index === 5 ? 'year' : ''} ${index === 0 ? 'current' : ''}`}
                                    style={{
                                      right: `${index * 18 + 5}%`,
                                    }}
                                  >
                                    {value.month}
                                    <br />
                                    <span className="year">
                                      {value.year}
                                    </span>
                                  </div>
                                  <div
                                    className="past_mark"
                                    style={{
                                      top: `${100 - (value.one_day_biz_point / (maxVerticalLabelBizPoint * 10000)) * 100}%`,
                                      right: `${index * 18 + 5}%`,
                                    }}
                                  />
                                  {
                                    index === 0 && (
                                      <div
                                        className="past_mark_current"
                                        style={{
                                          top: `${100 - (value.one_day_biz_point / (maxVerticalLabelBizPoint * 10000)) * 100}%`,
                                          right: `${index * 18 + 5}%`,
                                        }}
                                      />
                                    )
                                  }
                                  <div
                                    className={`past_point ${index === 0 ? 'current' : ''}`}
                                    style={{
                                      top: `${100 - (value.one_day_biz_point / (maxVerticalLabelBizPoint * 10000)) * 100}%`,
                                      right: `${index * 18 + 5}%`,
                                    }}
                                  >
                                    {Math.floor(value.one_day_biz_point).toLocaleString()}
                                  </div>
                                </React.Fragment>
                              ))
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </section>
              <section
                className="inner_contents"
                ref={subContentsLastRef}
              >
                <div className="horizontal_ruler" />
                <section className="page_contents">
                  <div className="content-block">
                    <div className="block-header">
                      <div className="title">
                        コミュニケーション
                      </div>
                    </div>
                    <div className="block-detail">
                      <DeviationHeader
                        target={target}
                        range="monthly"
                        work_style_id={workStyleId}
                        occupation_id={occupationId}
                      />
                      <Deviation
                        target={target}
                        title="calendar_scheduled_time"
                        display_title="カレンダー予定時間"
                        type="hour"
                        values={targetIndicatorValues("calendar_scheduled_time")}
                        labels={[0, 10, 20, 30, 40, 50, 60, 70, 80]}
                        upper_limit
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink={false}
                        help_text={t(`report.indicator_help.monthly.${target}.stress.calendar_scheduled_time`)}
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="communication_app_use_time"
                        display_prefix="コミュニケーションアプリ"
                        display_title="使用時間"
                        type="hour"
                        values={targetIndicatorValues("communication_app_use_time")}
                        labels={[0, 10, 20, 30, 40, 50, 60]}
                        upper_limit
                        appropriate={33.333}
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink
                        help_text={t(`report.indicator_help.monthly.${target}.stress.communication_app_use_time`)}
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="web_meeting_time"
                        display_prefix="Web会議"
                        display_title="会議時間"
                        type="hour"
                        values={targetIndicatorValues("web_meeting_time")}
                        labels={[0, 5, 10, 15, 20, 25, 30, 35, 40]}
                        upper_limit
                        appropriate={50}
                        appropriate_position={-25}
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink={false}
                        help_text={t(`report.indicator_help.monthly.${target}.stress.web_meeting_time`)}
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="web_meeting_count"
                        display_prefix="Web会議"
                        display_title="会議回数"
                        type="count"
                        values={targetIndicatorValues("web_meeting_count")}
                        labels={[0, 5, 10, 15, 20, 25, 30, 35, 40]}
                        upper_limit
                        appropriate={50}
                        appropriate_position={-25}
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink={false}
                        help_text={t(`report.indicator_help.monthly.${target}.stress.web_meeting_count`)}
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="long_web_meeting_count"
                        display_prefix="Web会議"
                        display_title="長時間会議回数"
                        type="count"
                        values={targetIndicatorValues("long_web_meeting_count")}
                        half
                        labels={[0, 2, 4, 6]}
                        upper_limit
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink={false}
                        help_text={t(`report.indicator_help.monthly.${target}.stress.long_web_meeting_count`)}
                        color={color}
                      />
                    </div>
                  </div>
                </section>
              </section>
            </section>
          </>
        )
      }
    </>
  )
}

export default MonthlyStress
